// src/pages/SelfService.tsx
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Container } from "react-bootstrap";
import CustomerForm from "../components/FormComponents/CustomerForm";
import axios from "axios";
import HeaderComponent from "../components/Header";
import SectionSelector from "../components/SectionSelector";
import GlobalModal from "../components/GlobalModal";
import ServicesCategorySection, {
  Service,
} from "../components/ServicesCategorySection";
import AccessibilityStatementModal from "../components/AccessibilityStatementModal";
import { PreBillPayment } from "./PreBillPayment";
import { Button } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";

const LPG_PARENT_KEY = "customerOwnershipOps_Lpg" as const;
const INVOICE_PAYMENT_KEY = "payBill_Lpg" as const;
const HIDDEN_PUBLIC_ACTION_IDS = [10001] as const;
const HIDDEN_LPG_SUBCATEGORY_ACTION_IDS = [17, 18] as const;
const LPG_SUBCATEGORY_STATIC_SERVICES: Service[] = [
  {
    id_action: 17,
    label: "סיום התקשרות",
    type: "url",
    value: "https://sfselfservice.supergas-power.co.il/s/st-landing-page",
    variant: "outlined",
  },
  {
    id_action: 18,
    label: "הצטרפות",
    type: "url",
    value: "https://sfselfservice.supergas-power.co.il/s/contactus", //"ownershipTransfer_Lpg",
    variant: "outlined",
  },
];

type SectionKey = "power" | "lpg";
type DeepLinkTarget = {
  action: string;
  section: SectionKey;
  hideChangePlanPowerItem?: boolean;
};

/** Deep-link mapping: /:serviceSlug → {action, section} */
const SERVICE_DEEP_LINKS: Record<string, DeepLinkTarget> = {
  "סיום-התקשרות": { action: "terminationOfService_Lpg", section: "lpg" },
  "החלפת-משלם": { action: "ownershipTransfer_Lpg", section: "lpg" },
  "בירור-ניוד": { action: "checkAboutMigrationProcess_Pwr", section: "power" },
  "חשבונית-גז": { action: "invOrRecRequest_Lpg", section: "lpg" },
  "תיאום-טכנאי": { action: "tecCheckSchedule_Lpg", section: "lpg" },
  "מסלול-חשמל": { action: "changePlan_Pwr", section: "power" },
  "מסלול-חשמל-חדש": {
    action: "changePlan_Pwr",
    section: "power",
    hideChangePlanPowerItem: true,
  },
  "מסלול-מאמי-למשפחה": { action: "mamiChangePlan_Pwr", section: "power" },
  "עדכון-מייל": { action: "updateEmail_Pwr", section: "power" },
  "חשבונית-חשמל": { action: "invoiceCopyRequest_Pwr", section: "power" },
  "עדכון-כרטיס": { action: "creditCardUpdateRequest_Pwr", section: "power" },
  "פעולות-בחשבון": { action: LPG_PARENT_KEY, section: "lpg" },
  "תשלום-חשבונית": { action: INVOICE_PAYMENT_KEY, section: "lpg" },
};

/** Reverse map so clicks update URL nicely */
const ACTION_TO_SLUG: Record<string, string> = Object.entries(
  SERVICE_DEEP_LINKS,
).reduce<Record<string, string>>((acc, [slug, v]) => {
  if (!acc[v.action]) acc[v.action] = slug;
  return acc;
}, {});

function normalizeSlug(s: string) {
  try {
    return decodeURIComponent(s).trim().toLowerCase();
  } catch {
    return (s || "").trim().toLowerCase();
  }
}

const SERVICE_DEEP_LINKS_NORM: Record<string, DeepLinkTarget> =
  Object.fromEntries(
    Object.entries(SERVICE_DEEP_LINKS).map(([k, v]) => [normalizeSlug(k), v]),
  );

export default function SelfService() {
  const { serviceSlug } = useParams<{ serviceSlug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Base path of THIS page (important!)
   * If route is /self-service/:serviceSlug? then:
   * - when on /self-service           → basePath = "/self-service"
   * - when on /self-service/תשלום-... → basePath = "/self-service"
   */
  const basePath = useMemo(() => {
    const path = location.pathname || "/";
    if (!serviceSlug) return path;
    const idx = path.lastIndexOf("/");
    return idx > 0 ? path.slice(0, idx) : "/";
  }, [location.pathname, serviceSlug]);

  const [activeSection, setActiveSection] = useState<SectionKey | null>(null);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [deepLinkTarget, setDeepLinkTarget] = useState<DeepLinkTarget | null>(
    null,
  );

  const headerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const lpgSectionRef = useRef<HTMLDivElement>(null);
  const lpgHiddenRef = useRef<HTMLDivElement>(null);
  const powerSectionRef = useRef<HTMLDivElement>(null);

  const [modal, setModal] = useState<{
    open: boolean;
    type?: "success" | "error" | "info";
    title?: string;
    subtitle?: string;
    specialInfo?: string;
  }>({ open: false });

  const showModal = ({
    type = "info",
    title = "",
    subtitle = "",
    specialInfo = "",
  }: {
    type?: "success" | "error" | "info";
    title?: string;
    subtitle?: string;
    specialInfo?: string;
  }) => setModal({ open: true, type, title, subtitle, specialInfo });

  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const [showAccessibility, setShowAccessibility] = useState(false);

  const scrollWithOffset = useCallback((el: HTMLElement | null, extra = 12) => {
    if (!el) return;
    const headerH = headerRef.current?.offsetHeight ?? 0;
    const y = el.getBoundingClientRect().top + window.scrollY - headerH - extra;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, []);

  // --- Fetch services ---
  const fetchServices = useCallback(async (category: SectionKey) => {
    setLoading(true);
    setAllServices([]);
    setSelectedService(null);
    try {
      const res = await axios.get(`api/services?section=${category}`);
      setAllServices((res.data || []) as Service[]);
    } catch {
      setAllServices([]);
    }
    setLoading(false);
  }, []);

  const handleSectionClick = async (section: SectionKey) => {
    setActiveSection(section);
    await fetchServices(section);
    // Clear slug (stay on the page base, not the site root)
    navigate(basePath, { replace: true });
  };

  const handleBackToSections = () => {
    setActiveSection(null);
    setAllServices([]);
    setSelectedService(null);
    navigate(basePath, { replace: true });
  };

  const handleBackToServices = () => {
    setSelectedService(null);
    // Return to section root (no specific slug)
    navigate(basePath, { replace: true });
  };

  /** Single place to handle selecting a service (clicks or deep-links) */
  const handleServiceSelect = useCallback(
    (srv: Service) => {
      const isInvoicePayment = srv.value === INVOICE_PAYMENT_KEY;

      // GTM
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "service_button_click",
        service_label: srv.label,
        service_type: srv.type,
      });

      // 🔴 URL SERVICES: open & STOP
      if (srv.type === "url" && !isInvoicePayment) {
        const serviceUrl = String(srv.value || "");
        window.open(serviceUrl, "_blank", "noopener,noreferrer");
        return; // ⛔ prevents navigation & state updates
      }

      // Normal SPA services
      setSelectedService(srv);

      const slug = ACTION_TO_SLUG[srv.value as string];
      if (slug) {
        const next = basePath.endsWith("/")
          ? `${basePath}${slug}`
          : `${basePath}/${slug}`;
        navigate(next, { replace: true });
      }
    },
    [navigate, basePath],
  );

  // --- LPG logic ---
  const visibleServices = useMemo(() => {
    const publiclyVisibleServices = allServices.filter(
      (s) => !HIDDEN_PUBLIC_ACTION_IDS.includes(s.id_action as any),
    );

    if (activeSection !== "lpg") return publiclyVisibleServices;
    // when selecting parent, hide all normal LPG services
    if (selectedService?.value === LPG_PARENT_KEY) return [];
    return publiclyVisibleServices.filter(
      (s) => !HIDDEN_LPG_SUBCATEGORY_ACTION_IDS.includes(s.id_action as any),
    );
  }, [allServices, activeSection, selectedService]);

  const isShowingLpgSubcategory =
    activeSection === "lpg" && selectedService?.value === LPG_PARENT_KEY;

  useEffect(() => {
    if (isShowingLpgSubcategory)
      requestAnimationFrame(() => scrollWithOffset(lpgHiddenRef.current));
  }, [isShowingLpgSubcategory, scrollWithOffset]);

  // --- Visibility states ---
  const showSections = !activeSection;
  const showServices =
    activeSection && !selectedService && !isShowingLpgSubcategory;

  const showLpgSubcategory = isShowingLpgSubcategory;

  const isInvoicePaymentService =
    selectedService?.value === INVOICE_PAYMENT_KEY;

  const showServiceForm =
    activeSection &&
    selectedService &&
    selectedService.type === "function" &&
    !isShowingLpgSubcategory &&
    !isInvoicePaymentService;

  const showInvoicePayment =
    activeSection &&
    selectedService &&
    isInvoicePaymentService &&
    !isShowingLpgSubcategory;

  // --- Scroll to form when showing form ---
  useEffect(() => {
    if (
      (selectedService?.type === "function" || isInvoicePaymentService) &&
      formRef.current
    ) {
      requestAnimationFrame(() => scrollWithOffset(formRef.current));
    }
  }, [selectedService, scrollWithOffset, isInvoicePaymentService]);

  // --- Deep-link parsing (path slug or ?service= query) ---
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const querySlug = query.get("service") || "";
    const raw = serviceSlug || querySlug || "";
    const slug = normalizeSlug(raw);

    if (!slug) {
      setDeepLinkTarget(null);
      return;
    }

    const target = SERVICE_DEEP_LINKS_NORM[slug];
    if (!target) return;

    setDeepLinkTarget(target);

    // Activate the right section (fetch happens in the next effect if needed)
    if (activeSection !== target.section) {
      setActiveSection(target.section);
    }
  }, [serviceSlug, location.search, activeSection]);

  // --- Fulfill deep-link once services are available ---
  useEffect(() => {
    if (!deepLinkTarget) return;

    // Ensure services for the section are loaded
    if (
      activeSection === deepLinkTarget.section &&
      allServices.length === 0 &&
      !loading
    ) {
      fetchServices(deepLinkTarget.section);
      return;
    }

    if (activeSection !== deepLinkTarget.section || allServices.length === 0) {
      return;
    }

    const srv = allServices.find((s) => s.value === deepLinkTarget.action);
    if (srv) {
      setSelectedService(srv);
    }
  }, [deepLinkTarget, activeSection, allServices, loading, fetchServices]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <GlobalModal
        open={modal.open}
        type={modal.type}
        title={modal.title || ""}
        subtitle={modal.subtitle}
        specialInfo={modal.specialInfo}
        onClose={closeModal}
      />

      <AccessibilityStatementModal
        open={showAccessibility}
        onClose={() => setShowAccessibility(false)}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={headerRef}>
          <HeaderComponent />
        </div>

        <Container
          style={{
            marginTop: 30,
            //  marginBottom: 40,
            flex: 1,
            maxWidth: "1190px",
          }}
          dir="rtl"
        >
          {/* --- SECTION SELECTOR --- */}
          {showSections && (
            <SectionSelector
              activeSection={activeSection}
              onSectionClick={handleSectionClick}
            />
          )}

          {/* --- BACK BUTTONS --- */}
          {activeSection && !showSections && !selectedService && (
            <Button
              variant="text"
              onClick={handleBackToSections}
              sx={{ mb: 0, fontWeight: 900, fontSize: 20 }}
            >
              → חזרה לתחומי השירות
            </Button>
          )}

          {selectedService && !isShowingLpgSubcategory && (
            <Button
              variant="text"
              onClick={handleBackToServices}
              sx={{ mb: 0, fontWeight: 900, fontSize: 20 }}
            >
              → חזרה לשירותים
            </Button>
          )}

          {isShowingLpgSubcategory && (
            <Button
              variant="text"
              onClick={handleBackToServices}
              sx={{ mb: 0, fontWeight: 900, fontSize: 20 }}
            >
              → חזרה לרשימת שירותי גז
            </Button>
          )}

          {/* --- NORMAL SERVICES --- */}
          {showServices && (
            <div className="row g-4 justify-content-center">
              {activeSection === "lpg" && (
                <div className="col-12 mx-auto" ref={lpgSectionRef}>
                  <ServicesCategorySection
                    isActive
                    onAvatarClick={() => {}}
                    services={visibleServices}
                    loading={loading}
                    onServiceSelect={handleServiceSelect}
                  />
                </div>
              )}

              {activeSection === "power" && (
                <div className="col-12 mx-auto" ref={powerSectionRef}>
                  <ServicesCategorySection
                    isActive
                    onAvatarClick={() => {}}
                    services={visibleServices}
                    loading={loading}
                    onServiceSelect={handleServiceSelect}
                  />
                </div>
              )}
            </div>
          )}

          {/* --- LPG SUBCATEGORY ONLY (after selecting parent) --- */}
          {showLpgSubcategory && (
            <div
              ref={lpgHiddenRef}
              className="row g-4 justify-content-center"
              style={{ maxWidth: 800, marginLeft: "auto", marginRight: "auto" }}
            >
              {LPG_SUBCATEGORY_STATIC_SERVICES.map((srv) => (
                <div
                  key={`${srv.value}-${srv.id_action}`}
                  className="col-12 col-sm-6"
                >
                  <Button
                    fullWidth
                    variant={srv.variant ?? "outlined"}
                    onClick={() => handleServiceSelect(srv)}
                    sx={{
                      mb: 2,
                      borderColor: "primary.main",
                      borderRadius: 3,
                      color: "primary.main",
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      textTransform: "none",
                      pl: 0,
                      minHeight: 56,
                      justifyContent: "center",
                      boxShadow: 2,
                    }}
                  >
                    <span style={{ color: "black" }}>{srv.label}</span>
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* --- SERVICE FORM --- */}
          {showServiceForm && (
            <div
              className="row g-4 justify-content-center"
              ref={formRef}
              style={{ scrollMarginTop: 120 }}
            >
              <div className="col-12 mx-auto">
                <CustomerForm
                  selectedService={selectedService!}
                  hideChangePlanPowerItem={
                    selectedService?.value === deepLinkTarget?.action &&
                    !!deepLinkTarget?.hideChangePlanPowerItem
                  }
                  showModal={showModal}
                />
              </div>
            </div>
          )}

          {/* --- INVOICE PAYMENT EMBED --- */}
          {showInvoicePayment && (
            <div
              className="row g-4 justify-content-center"
              ref={formRef}
              style={{ scrollMarginTop: 120 }}
            >
              <div className="col-12 mx-auto">
                <PreBillPayment />
              </div>
            </div>
          )}
        </Container>
      </div>

      <footer
        style={{
          backgroundColor: "gray",
          textAlign: "right",
          padding: "3px 5px",
          width: "100%",
        }}
      >
        <button
          type="button"
          onClick={() => setShowAccessibility(true)}
          style={{
            fontSize: "0.98rem",
            color: "white",
            textDecoration: "underline",
            marginRight: 5,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          הצהרת נגישות
        </button>
      </footer>
    </div>
  );
}
